import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  addLocation,
  setLocationActive,
  setLocationInactive,
  deleteLocation,
  setActiveMultiple,
  setInactiveMultiple,
  deleteMultiple,
  updateLocation,
} from "../redux/actions/locations";
import { ALL_LOCATIONS_REQUEST } from "../redux/constants/locations";

import { addLocationFormOptions, editLocationFormOptions } from "../services/formOptions";

import PageTitle from "../components/PageTitle";
import SubmitModal from "../containers/SubmitModal";
import CustomToolbar from "../components/CustomToolbar";
import Loader from "../components/Loader";
import EnlargedImageDialog from "../components/EnlargedImageDialog";
import ClickToEnlarge from "../components/ClickToEnlarge";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";

import MUIDataTable from "mui-datatables";
import TableTheme from "../utils/TableTheme";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";

const initialDialogOptions = {
  title: "",
  descriptionText: "",
  formOptions: {
    initialValues: {},
    fields: [],
    validationSchema: null,
  },
  open: false,
  submitHandler: () => {},
  handleClose: () => {},
};

const ManageLocationsPage = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.locations.locations);
  const requesting = useSelector((state) => state.request.requesting);

  const [currentDialogOptions, setCurrentDialogOptions] = React.useState(initialDialogOptions);

  const handleOpenDialog = ({ title, descriptionText, formOptions, submitHandler }) => {
    setCurrentDialogOptions({
      title: title,
      descriptionText: descriptionText,
      formOptions: formOptions,
      open: true,
      submitHandler: submitHandler,
      handleClose: handleCloseDialog,
    });
  };

  const handleCloseDialog = () => {
    setCurrentDialogOptions(initialDialogOptions);
  };

  const [enlargedImageDialogOpen, setEnlargedImageDialogOpen] = React.useState(false);
  const [currentEnlargedImage, setCurrentEnlargedImage] = React.useState("");

  const handleClickEnlargeImage = (imageLink) => {
    setEnlargedImageDialogOpen(true);
    setCurrentEnlargedImage(imageLink);
  };
  const handleCloseEnlargedImage = () => {
    setEnlargedImageDialogOpen(false);
    setCurrentEnlargedImage("");
  };
  const columns = [
    {
      name: "id",
      options: {
        download: false,
        filter: false,
        sort: false,
        display: "excluded",
      },
    },
    {
      name: "image",
      label: "Image",
      options: {
        download: false,
        sort: false,
        filter: false,
        customHeadRender: () => (
          <th key={0} style={{ borderBottom: "1px solid rgba(224, 224, 224, 1)" }} />
        ),
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <>
              <ClickToEnlarge imageLink={value} handleClickEnlargeImage={handleClickEnlargeImage} />
            </>
          );
        },
      },
    },
    {
      name: "name",
      label: "Name",
      options: {
        filter: false,
      },
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: false,
      },
    },
    {
      name: "isActive",
      label: "Status",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <FormControlLabel
              label={value ? "Active" : "Inactive"}
              value={value ? "Active" : "Inactive"}
              control={
                <Switch color="primary" checked={value} value={value ? "Active" : "Inactive"} />
              }
              onChange={(event) => {
                const id = tableMeta.rowData[0];
                if (event.target.value === "Active") {
                  dispatch(setLocationInactive(id));
                } else {
                  dispatch(setLocationActive(id));
                }
              }}
            />
          );
        },
      },
    },
    {
      name: "id",
      label: "Edit",
      options: {
        filter: false,
        sort: false,
        download: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                handleOpenDialog({
                  title: "Update Location",
                  descriptionText: "",
                  formOptions: {
                    ...editLocationFormOptions,
                    initialValues: {
                      name: tableMeta.rowData[2],
                      description: tableMeta.rowData[3],
                    },
                  },
                  submitHandler: (data) => {
                    updateLocationSubmitHandler(value, data);
                  },
                });
              }}
            >
              Edit
            </Button>
          );
        },
      },
    },
    {
      name: "id",
      label: "Delete",
      options: {
        filter: false,
        sort: false,
        download: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Button
              variant="outlined"
              color="secondary"
              onClick={(e) => {
                e.preventDefault();
                const confirmAction = window.confirm(
                  `Are you sure you want to delete location ${tableMeta.rowData[2]} ?This will lead to the removal of all the meeting rooms associated with these locations. This action can not be reversed.`
                );
                if (confirmAction) deleteLocationHandler(value);
              }}
            >
              Delete
            </Button>
          );
        },
      },
    },
  ];

  useEffect(() => {
    dispatch({ type: ALL_LOCATIONS_REQUEST });
  }, [dispatch]);

  if (requesting && !currentDialogOptions.open) {
    return <Loader />;
  }
  const handleMarkSelectedAsActive = (items) => {
    dispatch(setActiveMultiple(items));
  };
  const handleMarkSelectedAsInactive = (items) => {
    dispatch(setInactiveMultiple(items));
  };
  const handleDeleteSelected = (items) => {
    const confirmAction = window.confirm(
      `Are you sure you want to delete ${items.length} locations? This will lead to the removal of all the meeting rooms associated with this location. This action can not be reversed.`
    );
    if (confirmAction) dispatch(deleteMultiple(items));
  };

  const addLocationSubmitHandler = async (data) => {
    dispatch(addLocation(data));
  };
  const updateLocationSubmitHandler = async (id, data) => {
    dispatch(updateLocation(id, data));
  };

  const deleteLocationHandler = async (id) => {
    dispatch(deleteLocation(id));
  };

  const options = {
    filterType: "checkbox",
    print: false,
    sortOrder: {
      name: "name",
      direction: "asc",
    },
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => (
      <CustomToolbar
        selectedRows={selectedRows}
        displayData={displayData}
        setSelectedRows={setSelectedRows}
        handleMarkSelectedAsActive={handleMarkSelectedAsActive}
        handleMarkSelectedAsInactive={handleMarkSelectedAsInactive}
        handleDeleteSelected={handleDeleteSelected}
      />
    ),
  };

  return (
    <>
      <PageTitle
        title={"Manage Locations"}
        button={
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleOpenDialog({
                title: "Add new location",
                descriptionText: "",
                formOptions: addLocationFormOptions,
                submitHandler: addLocationSubmitHandler,
              });
            }}
          >
            Add new location
          </Button>
        }
      />
      <SubmitModal {...currentDialogOptions} />
      <EnlargedImageDialog
        open={enlargedImageDialogOpen}
        handleClose={handleCloseEnlargedImage}
        imageLink={currentEnlargedImage}
      />
      <MuiThemeProvider theme={TableTheme}>
        <MUIDataTable data={data} columns={columns} options={options} />
      </MuiThemeProvider>
    </>
  );
};

export default ManageLocationsPage;
